/**
 * Patient Calculations Utilities
 *
 * Shared utilities for calculating patient metrics from onboarding data.
 */

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth?: string | null): number | undefined {
  if (!dateOfBirth) return undefined;
  const birth = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Calculate BMI from height (cm) and weight (kg)
 */
export function calculateBMI(
  height?: number | null,
  weight?: number | null
): number | undefined {
  if (!height || !weight) return undefined;
  const heightInMeters = height / 100;
  return Math.round((weight / (heightInMeters * heightInMeters)) * 10) / 10;
}

/**
 * Journey Outcome Types
 *
 * UI outcome types for the JourneyCompleteCard component.
 * - "pregnant": Pregnancy confirmed (celebratory)
 * - "not_pregnant": Treatment completed without pregnancy (supportive)
 * - "paused": Journey temporarily paused
 * - "pending": Awaiting outcome / not yet recorded (neutral)
 */
export type JourneyOutcome = "pregnant" | "not_pregnant" | "paused" | "pending";

/**
 * API Cycle Outcome Type
 *
 * Outcome types as stored in the API/backend.
 */
type ApiCycleOutcomeType =
  | "cancelled"
  | "pregnancy"
  | "no_pregnancy"
  | "miscarriage"
  | "other"
  | null;

/**
 * Cycle data shape from patient.treatments[].cycles[]
 */
type CycleWithOutcome = {
  id?: string;
  status?: "open" | "closed" | null;
  outcome?: {
    type?: ApiCycleOutcomeType;
  } | null;
  startDate?: string | null;
};

/**
 * Treatment data shape from patient.treatments[]
 */
type TreatmentWithCycles = {
  id?: string;
  cycles?: CycleWithOutcome[] | null;
};

/**
 * Derive the journey outcome for the JourneyCompleteCard from patient treatment/cycle data.
 *
 * This function looks at the most recent completed cycle's outcome to determine
 * what state the journey complete card should display.
 *
 * Logic:
 * - If outcome.type === "pregnancy" → "pregnant"
 * - If outcome.type === "no_pregnancy" | "cancelled" | "miscarriage" | "other" → "not_pregnant"
 * - If no outcome recorded yet (null/undefined) → "pending"
 *
 * @param treatments - The patient's treatments array from usePatient()
 * @returns JourneyOutcome for the UI ("pregnant" | "not_pregnant" | "paused" | "pending")
 */
export function deriveJourneyOutcome(
  treatments: TreatmentWithCycles[] | null | undefined
): JourneyOutcome {
  if (!treatments || treatments.length === 0) {
    return "pending";
  }

  // Get the most recent treatment (last in array, or most recent by date)
  const currentTreatment = treatments[treatments.length - 1];

  if (!currentTreatment?.cycles || currentTreatment.cycles.length === 0) {
    return "pending";
  }

  // Get the most recent cycle (last in array, typically the active/completed one)
  // Sort by startDate descending to get most recent, or use last if no dates
  const sortedCycles = [...currentTreatment.cycles].sort((a, b) => {
    if (!a.startDate && !b.startDate) return 0;
    if (!a.startDate) return 1;
    if (!b.startDate) return -1;
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  });

  const mostRecentCycle = sortedCycles[0];

  // Check if cycle has an outcome recorded
  const outcomeType = mostRecentCycle?.outcome?.type;

  if (outcomeType === undefined || outcomeType === null) {
    // No outcome recorded yet - show pending state
    return "pending";
  }

  // Map API outcome type to UI outcome
  switch (outcomeType) {
    case "pregnancy":
      return "pregnant";
    case "no_pregnancy":
    case "cancelled":
    case "miscarriage":
    case "other":
      return "not_pregnant";
    default:
      // Unknown type - treat as pending to be safe
      return "pending";
  }
}

/**
 * Get treatment summary details from the most recent completed cycle.
 *
 * Returns details for displaying in TreatmentSummaryCard.
 */
export function getMostRecentCycleSummary(
  treatments: TreatmentWithCycles[] | null | undefined
): {
  cycleName: string;
  completedDate: string | null;
  outcome: JourneyOutcome;
} | null {
  if (!treatments || treatments.length === 0) {
    return null;
  }

  const currentTreatment = treatments[treatments.length - 1];

  if (!currentTreatment?.cycles || currentTreatment.cycles.length === 0) {
    return null;
  }

  // Get the most recent cycle
  const sortedCycles = [...currentTreatment.cycles].sort((a, b) => {
    if (!a.startDate && !b.startDate) return 0;
    if (!a.startDate) return 1;
    if (!b.startDate) return -1;
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  });

  const mostRecentCycle = sortedCycles[0];
  const cycleIndex = mostRecentCycle
    ? currentTreatment.cycles.indexOf(mostRecentCycle) + 1
    : 1;

  return {
    cycleName: `Cycle #${cycleIndex}`,
    completedDate: mostRecentCycle?.startDate || null,
    outcome: deriveJourneyOutcome(treatments),
  };
}
